/**
 * Header search dropdown: keyword suggestions + product cards.
 *
 * Replaces the instant-search list built into theme.js, which required more than three characters
 * (typing "inj" produced nothing) and rendered title + thumbnail only. This one starts at two
 * characters and shows what the DirectRC / HDRC dropdown shows: a suggestions column, then product
 * cards with SKU, price, sale and stock badges, and review stars.
 *
 * TWO SOURCES, ON PURPOSE
 * - Keyword suggestions come from Shopify's predictive-search API. It is the only source of query
 *   suggestions, and it returns each one with the matched run already wrapped in <mark>.
 * - Products come from templates/search.suggest.liquid, because the same API returns products with
 *   no SKU and no review data, and both appear on the card.
 * The two are fetched together and rendered when both settle, so the panel never reflows.
 */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 220;
  var QUERY_LIMIT = 6;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function stars(rating) {
    // Half stars are rounded to the nearest whole one; the row is decoration next to the count.
    var full = Math.round(Number(rating) || 0);
    return '★★★★★'.slice(0, full) + '☆☆☆☆☆'.slice(0, 5 - full);
  }

  function Panel(form, input) {
    this.form = form;
    this.input = input;
    this.node = el('div', 'psearch');
    this.node.setAttribute('role', 'listbox');
    this.node.hidden = true;
    form.appendChild(this.node);
    this.controller = null;
    this.timer = null;
    this.items = [];
    this.activeIndex = -1;
    this.bind();
  }

  Panel.prototype.bind = function () {
    var self = this;

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');

    // `input` rather than `keyup`: it also catches paste, drag-drop, and the clear button that
    // `type="search"` inputs render, none of which fire a key event.
    this.input.addEventListener('input', function () {
      clearTimeout(self.timer);
      var term = self.input.value.trim();
      if (term.length < MIN_CHARS) { self.close(); return; }
      self.timer = setTimeout(function () { self.search(term); }, DEBOUNCE_MS);
    });

    this.input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { self.close(); return; }
      if (!self.isOpen()) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); self.move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); self.move(-1); }
      else if (e.key === 'Enter' && self.activeIndex > -1) {
        e.preventDefault(); // let the highlighted row win instead of submitting the form
        self.items[self.activeIndex].click();
      }
    });

    document.addEventListener('click', function (e) {
      if (!self.form.contains(e.target)) self.close();
    });

    this.input.addEventListener('focus', function () {
      if (self.input.value.trim().length >= MIN_CHARS && self.node.childNodes.length) self.open();
    });
  };

  Panel.prototype.isOpen = function () { return this.node.classList.contains('is-open'); };

  Panel.prototype.open = function () {
    this.node.hidden = false;
    this.node.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
  };

  Panel.prototype.close = function () {
    this.node.classList.remove('is-open');
    this.node.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.activeIndex = -1;
  };

  Panel.prototype.move = function (step) {
    if (!this.items.length) return;
    if (this.activeIndex > -1) this.items[this.activeIndex].classList.remove('is-active');
    this.activeIndex += step;
    if (this.activeIndex < 0) this.activeIndex = this.items.length - 1;
    if (this.activeIndex >= this.items.length) this.activeIndex = 0;
    var node = this.items[this.activeIndex];
    node.classList.add('is-active');
    if (node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  };

  Panel.prototype.state = function (message) {
    this.node.innerHTML = '';
    this.node.appendChild(el('div', 'psearch__state', message));
    this.items = [];
    this.activeIndex = -1;
    this.open();
  };

  Panel.prototype.search = function (term) {
    var self = this;
    // Drop the previous round: without this a slow early request can land after a later one and
    // repaint the panel with results for a prefix the shopper has already typed past.
    if (this.controller) this.controller.abort();
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    this.controller = ctrl;
    var opts = ctrl ? { signal: ctrl.signal } : {};
    var q = encodeURIComponent(term);
    // Shopify's search matches whole tokens, so "moto" finds 2 products while "moto*" finds 167 and
    // "co" finds 1 against "co*"'s many. The dropdown, its product count and the "view all" link all
    // use the wildcard so the number shown is the number the results page then shows.
    var qWild = encodeURIComponent(/\*$/.test(term) ? term : term + '*');
    this.wildTerm = /\*$/.test(term) ? term : term + '*';

    this.state('Searching…');

    // Brackets are percent-encoded: an unencoded `resources[type]` is accepted by some clients and
    // proxies and rejected by others, which silently drops the whole suggestions column.
    var queries = fetch('/search/suggest.json?q=' + q +
        '&resources%5Btype%5D=query,collection&resources%5Blimit%5D=' + QUERY_LIMIT, opts)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var r = (d && d.resources && d.resources.results) || {};
        return { queries: r.queries || [], collections: r.collections || [] };
      })
      .catch(function () { return { queries: [], collections: [] }; });

    var products = fetch('/search?type=product&view=suggest&q=' + qWild, opts)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });

    Promise.all([queries, products]).then(function (out) {
      if (ctrl && ctrl.signal.aborted) return;
      self.render(term, out[0], out[1]);
    });
  };

  /**
   * Keyword phrases pulled out of the matched product titles.
   *
   * Shopify's query suggestions come from what shoppers have actually searched, so a term nobody has
   * typed yet returns nothing even when the catalogue covers it — "vor" gave one suggestion and
   * "118" one, against plenty of matching products. These fill the column from the titles instead:
   * the word that starts with what was typed, plus the word before it, which is usually the brand or
   * qualifier ("furitek vortex", "stinger 118").
   *
   * Terms the catalogue genuinely does not cover still come back empty, and should — "charger"
   * matches no product on this store at all.
   */
  function derive(term, products, taken) {
    var t = term.toLowerCase();
    var found = [];
    (products || []).forEach(function (p) {
      var words = String(p.title || '').toLowerCase().match(/[a-z0-9][a-z0-9/\-]*/g) || [];
      for (var i = 0; i < words.length; i++) {
        if (words[i].indexOf(t) !== 0) continue;
        var one = words[i];
        var two = (i > 0 && words[i - 1].length > 2) ? words[i - 1] + ' ' + one : null;
        [one, two].forEach(function (phrase) {
          if (!phrase) return;
          var key = phrase.toLowerCase();
          if (taken[key]) return;
          taken[key] = true;
          found.push(phrase);
        });
        break; // one phrase per title keeps the list varied rather than repeating a common word
      }
    });
    return found;
  }

  /** Highlight the typed run without handing untrusted text to innerHTML. */
  function highlight(node, label, term) {
    var at = label.toLowerCase().indexOf(term.toLowerCase());
    if (at < 0) { node.textContent = label; return; }
    node.appendChild(document.createTextNode(label.slice(0, at)));
    var m = document.createElement('mark');
    m.textContent = label.slice(at, at + term.length);
    node.appendChild(m);
    node.appendChild(document.createTextNode(label.slice(at + term.length)));
  }

  Panel.prototype.render = function (term, suggest, data) {
    var products = (data && data.products) || [];
    var total = data ? data.results_count : 0;

    // Priority: what shoppers really searched, then matching collections, then phrases lifted from
    // the titles — enough to fill the column whenever the catalogue has anything to offer.
    var taken = {};
    var items = [];
    (suggest.queries || []).forEach(function (q) {
      var key = (q.text || '').toLowerCase();
      if (!key || taken[key]) return;
      taken[key] = true;
      items.push({ label: q.text, styled: q.styled_text, url: q.url });
    });
    (suggest.collections || []).forEach(function (c) {
      var key = (c.title || '').toLowerCase();
      if (!key || taken[key] || items.length >= QUERY_LIMIT) return;
      taken[key] = true;
      items.push({ label: c.title, url: c.url });
    });
    derive(term, products, taken).forEach(function (phrase) {
      if (items.length >= QUERY_LIMIT) return;
      items.push({ label: phrase, url: '/search?type=product&q=' + encodeURIComponent(phrase + '*') });
    });

    var queries = items;

    if (!queries.length && !products.length) {
      this.state('No results for “' + term + '”');
      return;
    }

    this.node.innerHTML = '';
    this.items = [];
    this.activeIndex = -1;

    var body = el('div', 'psearch__body');

    if (queries.length) {
      var qcol = el('div', 'psearch__col psearch__col--queries');
      qcol.appendChild(el('p', 'psearch__heading', 'Popular suggestions'));
      var ul = el('ul', 'psearch__queries');
      queries.forEach(function (q) {
        var a = el('a', 'psearch__query');
        a.href = q.url || ('/search?type=product&q=' + encodeURIComponent(q.label + '*'));
        // styled_text is Shopify-generated markup limited to <span>/<mark>; anything derived here is
        // highlighted through the DOM instead so product text never reaches innerHTML.
        if (q.styled) a.innerHTML = q.styled; else highlight(a, q.label, term);
        a.setAttribute('role', 'option');
        var li = el('li');
        li.appendChild(a);
        ul.appendChild(li);
        this.items.push(a);
      }, this);
      qcol.appendChild(ul);
      body.appendChild(qcol);
    }

    if (products.length) {
      var pcol = el('div', 'psearch__col psearch__col--products');
      pcol.appendChild(el('p', 'psearch__heading', 'Product results'));
      var grid = el('div', 'psearch__grid');
      products.forEach(function (p) {
        grid.appendChild(this.card(p));
      }, this);
      pcol.appendChild(grid);
      body.appendChild(pcol);
    }

    this.node.appendChild(body);

    if (total) {
      var foot = el('div', 'psearch__footer');
      var all = el('a', 'psearch__viewall', 'View all results (' + total + ')');
      all.href = '/search?type=product&q=' + encodeURIComponent(this.wildTerm || term);
      all.setAttribute('role', 'option');
      foot.appendChild(all);
      this.node.appendChild(foot);
      this.items.push(all);
    }

    this.open();
  };

  Panel.prototype.card = function (p) {
    var a = el('a', 'psearch__card');
    a.href = p.url;
    a.setAttribute('role', 'option');

    var badges = el('div', 'psearch__badges');
    badges.appendChild(el('span', 'psearch__badge psearch__badge--' + (p.available ? 'stock' : 'out'),
      p.available ? 'In Stock' : 'Sold out'));
    if (p.discount_percent) {
      badges.appendChild(el('span', 'psearch__badge psearch__badge--sale', 'Sale ' + p.discount_percent + '%'));
    }
    a.appendChild(badges);

    if (p.image) {
      var img = el('img', 'psearch__thumb');
      img.src = p.image;
      img.alt = p.image_alt || p.title;
      img.loading = 'lazy';
      img.width = 200; img.height = 200;
      a.appendChild(img);
    }

    a.appendChild(el('p', 'psearch__title', p.title));
    if (p.sku) a.appendChild(el('p', 'psearch__sku', 'SKU: ' + p.sku));

    var price = el('div', 'psearch__price');
    if (p.compare_at_price) price.appendChild(el('del', null, p.compare_at_price));
    price.appendChild(el('span', null, p.price));
    a.appendChild(price);

    if (p.rating_count) {
      var r = el('div', 'psearch__rating');
      r.appendChild(el('span', 'psearch__stars', stars(p.rating)));
      r.appendChild(el('span', null, ' ' + p.rating_count + (p.rating_count == 1 ? ' review' : ' reviews')));
      a.appendChild(r);
    }
    return a;
  };

  function init() {
    var inputs = document.querySelectorAll('form[action*="/search"] input[name="q"]');
    if (!inputs.length) return;

    inputs.forEach(function (input) {
      var form = input.closest('form');
      if (!form || form.dataset.psearchReady) return;
      form.dataset.psearchReady = '1';

      // Stand down the theme's own instant search on this form, otherwise both dropdowns open at
      // once. theme.js binds its handlers on document-ready and appends a `.search-results` div, so
      // this runs on window load — after every ready handler — and removes both.
      if (window.jQuery) window.jQuery(input).off('keyup change');
      var old = form.querySelector('.search-results');
      if (old) old.parentNode.removeChild(old);

      new Panel(form, input);
    });
  }

  // Ready fires before theme.js's own ready handler may have bound; load is strictly after.
  if (document.readyState === 'complete') init();
  else window.addEventListener('load', init);
})();
